import { addDays, addMonths, addYears, format, isValid, parseISO, startOfDay, startOfMonth, startOfWeek, startOfYear, subDays, subMonths, subWeeks } from "date-fns";
import { DateFilterKey, DatePresetKey, DateRange, GlobalFilterValues } from "./types";

// Catálogo completo (labels pt-PT) — semântica canónica: presets "dias" = janela móvel
// incluindo hoje; "última semana/último mês" = período completo anterior; "meses/ano" =
// meses de calendário incluindo o mês corrente parcial.
export const DATE_FILTER_LABELS: Record<DateFilterKey, string> = {
    hoje: "Hoje",
    ultimos_7_dias: "Últimos 7 dias",
    ultimos_14_dias: "Últimos 14 dias",
    esta_semana: "Esta semana",
    ultima_semana: "Última semana",
    este_mes: "Este mês",
    ultimo_mes: "Último mês",
    ultimos_30_dias: "Últimos 30 dias",
    ultimos_60_dias: "Últimos 60 dias",
    ultimos_3_meses: "Últimos 3 meses",
    ultimos_6_meses: "Últimos 6 meses",
    este_ano: "Este ano",
    ultimos_12_meses: "Últimos 12 meses",
    ultimos_13_meses: "Últimos 13 meses",
    ultimos_30_dias_ate_ontem: "Últimos 30 dias (até ontem)",
    ultimos_7_dias_ate_ontem: "Últimos 7 dias (até ontem)",
    esta_semana_ate_ontem: "Esta semana (até ontem)",
    este_mes_ate_ontem: "Este mês (até ontem)",
    sem_periodo: "Sem período",
    personalizado: "Personalizado",
};

// Subset por omissão (dashboards sem `presets` na config) — inalterado para manter os
// dashboards existentes visualmente iguais.
export const DEFAULT_DATE_FILTER_PRESETS: Array<DateFilterKey> = ["hoje", "esta_semana", "este_mes", "ultimos_30_dias", "este_ano", "ultimos_12_meses"];

export const DATE_PRESET_OPTIONS: Array<{ value: DatePresetKey; label: string }> = [
    { value: "hoje", label: "Hoje" },
    { value: "esta_semana", label: "Esta semana" },
    { value: "este_mes", label: "Este mês" },
    { value: "ultimos_30_dias", label: "Últimos 30 dias" },
    { value: "este_ano", label: "Este ano" },
    { value: "ultimos_12_meses", label: "Últimos 12 meses" },
];

const formatDay = (date: Date) => {
    const formatted = format(date, "yyyy-MM-dd");
    return formatted;
};

export const resolveDatePreset = (preset: DatePresetKey, now: Date): DateRange => {
    const today = startOfDay(now);

    if (preset === "hoje") {
        return { gte: formatDay(today), lt: formatDay(addDays(today, 1)) };
    }
    if (preset === "ultimos_7_dias") {
        // 7 dias INCLUINDO hoje — mesma semântica do ultimos_30_dias.
        return { gte: formatDay(subDays(today, 6)), lt: formatDay(addDays(today, 1)) };
    }
    if (preset === "ultimos_14_dias") {
        // 14 dias INCLUINDO hoje (mesma semântica Zoho "Últimos N dias").
        return { gte: formatDay(subDays(today, 13)), lt: formatDay(addDays(today, 1)) };
    }
    if (preset === "esta_semana") {
        const weekStart = startOfWeek(today, { weekStartsOn: 1 });
        return { gte: formatDay(weekStart), lt: formatDay(addDays(weekStart, 7)) };
    }
    if (preset === "ultima_semana") {
        const weekStart = startOfWeek(today, { weekStartsOn: 1 });
        return { gte: formatDay(subDays(weekStart, 7)), lt: formatDay(weekStart) };
    }
    if (preset === "ultimo_mes") {
        const monthStart = startOfMonth(today);
        return { gte: formatDay(subMonths(monthStart, 1)), lt: formatDay(monthStart) };
    }
    if (preset === "ultimos_30_dias") {
        // 30 dias INCLUINDO hoje (semântica Zoho "Últimos N dias", validada vs gauge svc39: 73)
        return { gte: formatDay(subDays(today, 29)), lt: formatDay(addDays(today, 1)) };
    }
    if (preset === "ultimos_60_dias") {
        // 60 dias INCLUINDO hoje (mesma semântica dos restantes "Últimos N dias").
        return { gte: formatDay(subDays(today, 59)), lt: formatDay(addDays(today, 1)) };
    }
    if (preset === "ultimos_3_meses") {
        const monthStart = startOfMonth(today);
        return { gte: formatDay(subMonths(monthStart, 2)), lt: formatDay(addMonths(monthStart, 1)) };
    }
    if (preset === "ultimos_6_meses") {
        // 6 meses de calendário incluindo o mês corrente parcial (semântica Zoho "Últimos 6 meses").
        const monthStart = startOfMonth(today);
        return { gte: formatDay(subMonths(monthStart, 5)), lt: formatDay(addMonths(monthStart, 1)) };
    }
    if (preset === "este_ano") {
        const yearStart = startOfYear(today);
        return { gte: formatDay(yearStart), lt: formatDay(addYears(yearStart, 1)) };
    }
    if (preset === "ultimos_12_meses") {
        const monthStart = startOfMonth(today);
        return { gte: formatDay(subMonths(monthStart, 11)), lt: formatDay(addMonths(monthStart, 1)) };
    }
    if (preset === "ultimos_13_meses") {
        // 13 meses de calendário: 12 meses completos anteriores + o mês corrente parcial (semântica
        // Zoho "Últimos 13 meses" — as médias mensais dividem por 13 incluindo o mês parcial).
        const monthStart = startOfMonth(today);
        return { gte: formatDay(subMonths(monthStart, 12)), lt: formatDay(addMonths(monthStart, 1)) };
    }
    // Variantes "até ontem": excluem sempre hoje/o futuro (lt = hoje). Servem dashboards cujo
    // rácio compara realizado vs planeado (ex.: nfb-productivity, sessões feitas ÷ horário) —
    // dias ainda por cumprir não têm sessões feitas e enviesariam a métrica.
    if (preset === "ultimos_30_dias_ate_ontem") {
        return { gte: formatDay(subDays(today, 30)), lt: formatDay(today) };
    }
    if (preset === "ultimos_7_dias_ate_ontem") {
        return { gte: formatDay(subDays(today, 7)), lt: formatDay(today) };
    }
    if (preset === "esta_semana_ate_ontem") {
        const weekStart = startOfWeek(today, { weekStartsOn: 1 });
        return { gte: formatDay(weekStart), lt: formatDay(today) };
    }
    if (preset === "este_mes_ate_ontem") {
        const monthStart = startOfMonth(today);
        return { gte: formatDay(monthStart), lt: formatDay(today) };
    }

    const monthStart = startOfMonth(today);
    return { gte: formatDay(monthStart), lt: formatDay(addMonths(monthStart, 1)) };
};

const isResolvablePreset = (value: string): value is DatePresetKey => {
    const resolvable = value in DATE_FILTER_LABELS && value !== "sem_periodo" && value !== "personalizado";
    return resolvable;
};

const isValidDayString = (day: string) => {
    const parsed = parseISO(day);
    const valid = /^\d{4}-\d{2}-\d{2}$/.test(day) && isValid(parsed);
    return valid;
};

// Único caminho para transformar valores crus do filtro em range. NUNCA faz fallback
// silencioso: valor desconhecido/ausente, sem_periodo ou personalizado incompleto → null.
// As chaves derivadas `${key}_from`/`${key}_to` (yyyy-MM-dd, "Até" inclusivo) só são lidas
// quando o valor é "personalizado" — valores obsoletos ficam ignorados com um preset ativo.
export const resolveDateFilterRange = (values: GlobalFilterValues, filterKey: string, now: Date): DateRange | null => {
    const value = values[filterKey];
    if (typeof value !== "string" || value === "" || value === "sem_periodo") return null;

    if (value === "personalizado") {
        const fromDay = values[`${filterKey}_from`];
        const toDay = values[`${filterKey}_to`];
        if (typeof fromDay !== "string" || typeof toDay !== "string") return null;
        if (!isValidDayString(fromDay) || !isValidDayString(toDay) || fromDay > toDay) return null;
        return { gte: fromDay, lt: formatDay(addDays(parseISO(toDay), 1)) };
    }

    if (!isResolvablePreset(value)) return null;
    const range = resolveDatePreset(value, now);
    return range;
};

// Variante não-nullable para dashboards cujo subset não inclui sem_periodo — espelha o
// padrão atual `(values.timeline as DatePresetKey) || "este_ano"`.
export const resolveRequiredDateFilterRange = (values: GlobalFilterValues, filterKey: string, fallbackPreset: DatePresetKey, now: Date): DateRange => {
    const resolved = resolveDateFilterRange(values, filterKey, now);
    const range = resolved ?? resolveDatePreset(fallbackPreset, now);
    return range;
};

const isSunday = (date: Date) => {
    const sunday = date.getDay() === 0;
    return sunday;
};

// Réplica da função business_days(início, fim, 1) do Zoho: conta os dias em [gte, lt) excluindo domingos
// (validado via query_data: jun 2026 [01,30)=25, mai [01,31)=26, fev [01,28)=23).
export const businessDaysExcludingSundays = (range: DateRange) => {
    let businessDays = 0;
    const lastDayExclusive = parseISO(range.lt);
    for (let day = parseISO(range.gte); day < lastDayExclusive; day = addDays(day, 1)) {
        if (!isSunday(day)) businessDays += 1;
    }
    return businessDays;
};

const isWeekend = (date: Date) => {
    const weekend = date.getDay() === 0 || date.getDay() === 6;
    return weekend;
};

const nextCountedDaysRange = (start: Date, countedDayCount: number, isSkippedDay: (date: Date) => boolean) => {
    const lastCountedDay = new Date(start.getTime());
    let remainingCountedDays = isSkippedDay(lastCountedDay) ? countedDayCount : countedDayCount - 1;
    while (remainingCountedDays > 0) {
        lastCountedDay.setDate(lastCountedDay.getDate() + 1);
        if (!isSkippedDay(lastCountedDay)) remainingCountedDays -= 1;
    }
    const range: DateRange = { gte: formatDay(start), lt: formatDay(addDays(lastCountedDay, 1)) };
    return range;
};

// Window `[gte, lt)` covering the next `workDayCount` work days from `start`, counting `start` itself when
// it is a work day. The clinic opens on SATURDAY, so only Sundays are skipped — same rule as
// businessDaysExcludingSundays above. Base of the "próximos N dias úteis" gauges.
export const nextWorkDaysRange = (start: Date, workDayCount: number) => {
    const range = nextCountedDaysRange(start, workDayCount, isSunday);
    return range;
};

// Monday-to-Friday variant, for the few metrics whose business definition leaves Saturday out. The range is
// still contiguous, so it can contain a weekend: the caller must also filter Saturday rows out.
export const nextWeekdaysRange = (start: Date, weekdayCount: number) => {
    const range = nextCountedDaysRange(start, weekdayCount, isWeekend);
    return range;
};

export const comparisonWeekPeriods = (now: Date) => {
    const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
    const previousWeekStart = subWeeks(currentWeekStart, 1);

    const periods = {
        current: { gte: formatDay(currentWeekStart), lt: formatDay(addDays(currentWeekStart, 7)) },
        previous: { gte: formatDay(previousWeekStart), lt: formatDay(currentWeekStart) },
    };
    return periods;
};
